import { ConnectedPosition, OverlayModule, ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
	AfterContentInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EmbeddedViewRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	OnDestroy,
	Output,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SpotlightKey } from '@reach/interfaces/spotlights/spotlights.enum';

import { Implicit } from '~app-client/modals/types';
import { ONBOARDING_CONFIG_MAP } from '~app-client/onboarding/constants';
import { OnboardingService } from '~app-client/onboarding/services';
import { OnboardingMessageContext, OnboardingStatus } from '~app-client/onboarding/types';
import { getConfigItem, isValidOnboardingKey } from '~app-client/onboarding/utils';

import { OnboardingSpotlightMessageComponentModule } from '../spotlight-message/spotlight-message.component';
import { OnboardingSpotlightPointComponentModule } from '../spotlight-point/spotlight-point.component';

const MESSAGE_SPACING = 16;

@Component({
	selector: 'onboarding-spotlight-target, [onboardingSpotlightTarget]',
	templateUrl: './spotlight-target.component.html',
	styleUrls: ['./spotlight-target.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		OverlayModule,
		OnboardingSpotlightPointComponentModule,
		OnboardingSpotlightMessageComponentModule,
	],
})
export class OnboardingSpotlightTargetComponentModule implements AfterContentInit, OnDestroy {
	private readonly scrollStrategyOptions = inject(ScrollStrategyOptions);
	private readonly onboardingService = inject(OnboardingService);
	public readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

	private contentInitialized = false;

	private _onboardingKey?: SpotlightKey;

	public readonly _pointPosition: ConnectedPosition[] = [
		{
			originX: 'center',
			originY: 'center',
			overlayX: 'center',
			overlayY: 'center',
		},
	];

	public _messagePosition: ConnectedPosition[] = [
		{
			originX: 'center',
			originY: 'center',
			overlayX: 'center',
			overlayY: 'bottom',
		},
	];

	public _messageOffsetXMultiplier = 0;
	public _messageOffsetYMultiplier = 0;

	public readonly scrollStrategy = this.scrollStrategyOptions.reposition();

	@Input()
	public closeOnClick = true;

	@Input()
	public set onboardingKey(onboardingKey: SpotlightKey | undefined) {
		if (onboardingKey === this.onboardingKey) {
			return;
		}

		// remove previous key if needed
		if (isValidOnboardingKey(this.onboardingKey)) {
			this.removeOnboarding();
		}

		this._onboardingKey = onboardingKey;

		// new one
		if (isValidOnboardingKey(onboardingKey)) {
			void this.checkAvailability();
		} else if (onboardingKey as string | undefined) {
			console.warn(`Onboarding key ${onboardingKey} is not a valid key`);
		}
	}

	public get onboardingKey(): SpotlightKey | undefined {
		return this._onboardingKey;
	}

	@ViewChild('vcr', { read: ViewContainerRef })
	private vcr?: ViewContainerRef;

	@ViewChild('pointTpl')
	private pointTpl?: TemplateRef<void>;

	@ViewChild('messageTpl')
	private messageTpl?: TemplateRef<Implicit<OnboardingMessageContext>>;

	private get nativeElement(): HTMLElement {
		return this.elementRef.nativeElement;
	}

	private pointEv?: EmbeddedViewRef<void>;
	private messageEv?: EmbeddedViewRef<Implicit<OnboardingMessageContext>>;

	private messageTrianglePositionCalculated = false;
	private messageTriangleLeft?: number;
	private messageTriangleTop?: number;

	private somethingShown = false;

	private _requestId = 0;
	private _destroyed = false;

	@Output('popupDoneClicked')
	private popupDoneClicked$$ = new EventEmitter<boolean>();

	ngAfterContentInit(): void {
		this.contentInitialized = true;
		void this.checkAvailability();
	}

	ngOnDestroy(): void {
		this._destroyed = true;
		this.removeOnboarding();
	}

	@HostListener('click')
	public __onClick(): void {
		if (this.somethingShown && this.closeOnClick && this.onboardingKey) {
			void this.onboardingService.markAsSeen(this.onboardingKey, this.somethingShown);
		}
	}

	public async showModal(): Promise<void> {
		if (!this.onboardingKey) {
			return;
		}

		const ret = await this.onboardingService.showOnboardingModal(this.onboardingKey);
		if (ret) {
			this.popupDoneClicked$$.emit();
		}
	}

	public _messageLeftPx(message: HTMLElement): number {
		if (this.messageTrianglePositionCalculated) {
			return this.messageTriangleLeft ?? 0;
		}

		requestAnimationFrame(() => {
			if (this.messageTrianglePositionCalculated || !this.onboardingKey) {
				return;
			}

			const messageBox = message.getBoundingClientRect();
			const targetBox = this.nativeElement.getBoundingClientRect();

			const config = ONBOARDING_CONFIG_MAP[this.onboardingKey];

			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (!config) {
				return;
			}

			const messagePosition = getConfigItem(config.messagePosition);

			const extraChecks = messagePosition?.overlayX === 'center'; // top or bot
			const extraSpace = extraChecks ? MESSAGE_SPACING : 0;

			const messageLeft = messageBox.left + extraSpace;
			const messageRight = messageBox.left + messageBox.width - extraSpace;

			const targetLeft = targetBox.left;
			const targetCenter = targetLeft + targetBox.width / 2;

			const screenLeft = Math.max(messageLeft, Math.min(targetCenter, messageRight));

			const ret = screenLeft - messageLeft + extraSpace;

			this.messageTriangleLeft = ret;
		});

		return this.messageTriangleLeft ?? 0;
	}

	public _messageTopPx(message: HTMLElement): number {
		if (this.messageTrianglePositionCalculated) {
			return this.messageTriangleTop ?? 0;
		}

		requestAnimationFrame(() => {
			if (this.messageTrianglePositionCalculated || !this.onboardingKey) {
				return;
			}

			const messageBox = message.getBoundingClientRect();
			const targetBox = this.nativeElement.getBoundingClientRect();

			const config = ONBOARDING_CONFIG_MAP[this.onboardingKey];

			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (!config) {
				return;
			}

			const messagePosition = getConfigItem(config.messagePosition);

			const extraChecks = messagePosition?.overlayY === 'center'; // left or right
			const extraSpace = extraChecks ? MESSAGE_SPACING : 0;

			const messageTop = messageBox.top + extraSpace;
			const messageBottom = messageBox.top + messageBox.height - extraSpace;

			const targetTop = targetBox.top;
			const targetCenter = targetTop + targetBox.height / 2;

			const screenTop = Math.max(messageTop, Math.min(targetCenter, messageBottom));

			const ret = screenTop - messageTop + extraSpace;

			this.messageTriangleTop = ret;
			this.messageTrianglePositionCalculated = true;
			this.messageEv?.detectChanges();
		});

		return this.messageTriangleTop ?? 0;
	}

	public _onMessagePositionChanged(): void {
		this.messageTrianglePositionCalculated = false;
		this.messageEv?.detectChanges();
	}

	public _onMessageBlurred(): void {
		this.messageEv?.destroy();
		delete this.messageEv;

		if (!this.onboardingKey) {
			return;
		}

		const config = ONBOARDING_CONFIG_MAP[this.onboardingKey];
		if (!config.withPoint) {
			void this.onboardingService.markAsSeen(this.onboardingKey);
		}
	}

	public _markAsSeen(): void {
		if (!this.onboardingKey) {
			return;
		}

		void this.onboardingService.markAsSeen(this.onboardingKey, true);
	}

	private async checkAvailability(): Promise<void> {
		if (this.contentInitialized && !!this.onboardingKey) {
			const key = this.onboardingKey;

			this._requestId += 1;
			const id = this._requestId;

			const status = await firstValueFrom(
				this.onboardingService.keyStatus$(key).pipe(
					filter((iStatus) => {
						return [OnboardingStatus.AVAILABLE, OnboardingStatus.ALREADY_SEEN].includes(
							iStatus
						);
					})
				)
			);

			if (status === OnboardingStatus.AVAILABLE) {
				await new Promise((res) => setTimeout(res, 500));

				const newStatus = await firstValueFrom(this.onboardingService.keyStatus$(key));

				if (newStatus !== status) {
					return await this.checkAvailability();
				}
			}

			if (this._destroyed || id !== this._requestId) {
				return;
			}

			const available = status === OnboardingStatus.AVAILABLE;

			if (key === this.onboardingKey) {
				if (available) {
					void this.configureKey(key);
				} else {
					this.removeOnboarding();
				}
			}

			void this.onboardingService
				.waitUntilOnboardingStatus(key, [
					OnboardingStatus.ALREADY_SEEN,
					OnboardingStatus.WAITING_FOR_PREVIOUS_KEYS,
					OnboardingStatus.WAITING_FOR_CONDITION,
				])
				.then((iStatus) => {
					if (this.onboardingKey === key) {
						this.removeOnboarding();

						// If it's not met by a condition, restart the loop.
						if (iStatus !== OnboardingStatus.ALREADY_SEEN) {
							void this.checkAvailability();
						}
					}
				});
		}
	}

	private async configureKey(key: SpotlightKey): Promise<void> {
		if (!this.vcr) {
			return;
		}

		const config = ONBOARDING_CONFIG_MAP[key];

		this.vcr.clear();

		if (config.withPoint && this.pointTpl) {
			this.pointEv = this.vcr.createEmbeddedView(this.pointTpl);
			this.pointEv.detectChanges();
			this.somethingShown = true;
		}

		if (config.withMessage && this.messageTpl) {
			try {
				const data = await this.onboardingService.getMessage(key);

				const messagePosition = getConfigItem(config.messagePosition);

				this._messagePosition = [
					{
						...this._messagePosition[0],
						overlayX: messagePosition.overlayX,
						overlayY: messagePosition.overlayY,
					},
				];

				this._messageOffsetXMultiplier =
					this._messagePosition[0].overlayX === 'center'
						? 0
						: this._messagePosition[0].overlayX === 'start'
							? 1
							: -1;

				this._messageOffsetYMultiplier =
					this._messagePosition[0].overlayY === 'center'
						? 0
						: this._messagePosition[0].overlayY === 'top'
							? 1
							: -1;

				this.messageEv = this.vcr.createEmbeddedView(this.messageTpl, {
					$implicit: {
						data,
						showFooter: config.withMoreInfoButton,
						showGotIt: config.gotItButton,
					},
				});
				this.messageEv.detectChanges();
				this.somethingShown = true;
			} catch (error) {
				console.error(error);
			}
		}

		if (config.justPopUp) {
			await this.showModal();
		}
	}

	private removeOnboarding(): void {
		this.messageEv?.destroy();
		this.pointEv?.destroy();
		this._requestId += 1;
		this.vcr?.clear();
		delete this.pointEv;
		delete this.messageEv;
		this.messageTrianglePositionCalculated = false;
		this.somethingShown = false;
	}
}
